import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MoviesSelectorMode, Shop, ShopMovie, ShopMovieFormat } from '@digilize/shared/definitions/src';

type ShopMovieSelectorOption = ShopMovie & {
  disabled?: boolean;
};

/** One option: a film, in one of its formats when `perFormat` is on. */
export interface MoviesSelectorEntry {
  movie: ShopMovieSelectorOption;
  format: ShopMovieFormat | null;
}

@Component({
  selector: 'lib-movies-selector',
  templateUrl: './movies-selector.component.html',
  styleUrls: ['./movies-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MoviesSelectorComponent implements OnInit, OnChanges {
  @ViewChild(MatAutocompleteTrigger) autoComplete: MatAutocompleteTrigger;

  @Output() searchChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() movieSelected: EventEmitter<ShopMovie> = new EventEmitter<ShopMovie>();
  /** With `perFormat`: the film and the format picked with it. */
  @Output() formatSelected = new EventEmitter<{ movie: ShopMovie; format: ShopMovieFormat | null }>();
  @Output() autocompleteClosed: EventEmitter<void> = new EventEmitter<void>();

  @Input() shop: Shop;
  @Input() options: ShopMovieSelectorOption[];
  /** PAUL-3747: lists a film once per format ("Film 2D", "Film 3D"); a film without formats stays one option. */
  @Input() perFormat = false;
  @Input() placeholder = 'global.add_movie';
  @Input() btnLabel = 'global.add';
  @Input() selectorMode: MoviesSelectorMode = MoviesSelectorMode.Selector;
  @Input() templateOptions: any[];
  @Input() keepOpen = false;
  optionsTemplate?: TemplateRef<any>;

  entries: MoviesSelectorEntry[] = [];
  selectedOption;
  searchControl = new FormControl();
  movies: ShopMovie[];
  dataLoaded = true;
  inputValue = '';

  constructor() {}

  ngOnInit() {}

  ngOnChanges() {
    this.entries = (this.options ?? []).flatMap((movie) =>
      this.perFormat && movie.formats?.length
        ? movie.formats.map((format) => ({ movie, format }))
        : [{ movie, format: null }]
    );
  }

  /** A film with a single format keeps its plain option unless that format is something other than 2D. */
  hide2dFor(entry: MoviesSelectorEntry): boolean {
    return (entry.movie.formats?.length ?? 0) <= 1;
  }

  trackByEntry(_: number, entry: MoviesSelectorEntry): string {
    return `${entry.movie.id}:${entry.format?.id ?? ''}`;
  }

  onValueChange(event) {
    this.inputValue = event.target.value;
    this.searchChange.emit(this.inputValue);
  }

  selectMovie(event, entry: MoviesSelectorEntry) {
    event.stopPropagation();
    const { movie, format } = entry;
    this.movieSelected.emit(movie);

    if (this.perFormat) {
      this.formatSelected.emit({ movie, format });
    }

    if (this.selectorMode === MoviesSelectorMode.Filter) {
      // the options of one film read alike, so the field names the format picked when there was a choice
      this.inputValue = format && movie.formats?.length > 1 ? `${movie.title} (${format.name})` : movie.title;
    }
    // this.autoComplete.closePanel();
    // setTimeout(() => {
    //   this.autoComplete.openPanel();
    //   console.log(this.autoComplete);
    // }, 500);
    // event.source._emitSelectEvent = false;

    if (!this.keepOpen) {
      this.autoComplete.closePanel();
    }
  }

  onAutocompleteClosed() {
    if (this.selectorMode === MoviesSelectorMode.Selector) {
      this.inputValue = '';
    }
    this.autocompleteClosed.emit();
  }

  optionClicked(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  resetValue(event) {
    event.stopPropagation();
    this.inputValue = '';
    this.searchChange.emit(this.inputValue);
  }

  displayFn(movie: ShopMovie): string {
    return movie?.title;
  }
}
